import { useState } from "react";

import useUpdateApplicationStatus from "@api/job/useUpdateApplicationStatus";
import {
  Sort as SortIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Avatar, IconButton, Stack, Typography } from "@mui/material";

import { useAppDispatch, useAppSelector } from "@app/hooks";

import { applicationActions } from "@features/application/slice";

import { CircleLoading } from "@components/Loading/CircleLoading";

import { formatDurationTimeFromNow } from "@utils/format/dateTime";
import { roundedEarningNumber } from "@utils/numberHelper";

import { ReactComponent as BookmarkIcon } from "@assets/icons/bookmark.svg";
import { ReactComponent as BookmarkFulFillIcon } from "@assets/icons/bookmark_fulfill.svg";

import { Application } from "@dto/application";

import { useSnackbar } from "@contexts/SnackbarContext";

import queryClient from "@config/queryClient";

const openAILogo =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQS3PwERLLNB9XKFpeMgAMPxl5VvN3HRJnXQQ&s";
export default function ApplicationItem({
  app,
  jobId,
}: {
  app: Application & { hasNewUpdate: boolean; model: string };
  jobId: string;
}) {
  const { filters, applicationId } = useAppSelector((state) => state.application);
  const dispatch = useAppDispatch();
  const [mouseOnApp, setMouseOnApp] = useState("");
  const { setSnackbar } = useSnackbar();
  const { mutateAsync: updateAppStatus, isLoading: isUpdateAppLoading } =
    useUpdateApplicationStatus();

  const handleUpdateAppStatus = async (
    e: React.MouseEvent<HTMLElement>,
    action: "save" | "close",
    statuses: { saved?: boolean; archived?: boolean },
    appId: string
  ) => {
    e?.stopPropagation();
    setMouseOnApp(appId);
    const itemStatuses = { ...statuses };

    if (action === "save") {
      itemStatuses.saved = !itemStatuses.saved;
      if (filters.type === "archived") {
        itemStatuses.archived = false;
      }
    } else if (action === "close") {
      if (!filters.type) {
        itemStatuses.archived = true;
      } else {
        itemStatuses[filters.type] = false;
      }
    }
    try {
      await updateAppStatus({ jobId, applicationId: appId, ...itemStatuses });
      queryClient.invalidateQueries(["getApplicationDetails", jobId, appId]);
      queryClient.invalidateQueries(["getApplicationsByJob", jobId, filters]);
    } catch (error: any) {
      console.log("ERR", error);
      setSnackbar({
        message: error?.response?.data?.message,
        open: true,
        severity: "error",
      });
    }
  };

  return (
    <Stack position={"relative"}>
      {app.type === "system_ai" ? (
        <Stack
          p={2}
          gap={1.4}
          sx={{
            cursor: "pointer",
            ":hover": { bgcolor: "common.deutziaWhite" },
            position: "relative",
          }}
          bgcolor={(applicationId === app.id || !app.isRead) && "common.deutziaWhite"}
          onClick={() => dispatch(applicationActions.setApplicationId(app.id))}
        >
          <Stack
            flexDirection={"row"}
            gap={1.2}
            width={"calc(100% - 26px)"}
            alignItems={"center"}
          >
            <Avatar
              src={openAILogo}
              sx={{
                width: 58,
                height: 58,
                borderRadius: "50%",
              }}
            />
            <Stack gap={0.4}>
              <Typography color={"#3182CE"} fontWeight={700}>
                OpenAI
              </Typography>
            </Stack>
          </Stack>
          <Typography fontSize={14} fontWeight={700}>
            Model: {app.model}
          </Typography>
          <Typography fontSize={14}>Description: {app.description}</Typography>
        </Stack>
      ) : (
        <>
          <Stack
            p={2}
            gap={1.4}
            sx={{
              cursor: "pointer",
              ":hover": { bgcolor: "common.deutziaWhite" },
              position: "relative",
            }}
            bgcolor={(applicationId === app.id || !app.isRead) && "common.deutziaWhite"}
            onClick={() => dispatch(applicationActions.setApplicationId(app.id))}
          >
            <IconButton
              sx={{ position: "absolute", top: 8, right: 10 }}
              onClick={(e) =>
                handleUpdateAppStatus(
                  e,
                  "close",
                  { saved: app?.saved, archived: app?.archived },
                  app.id
                )
              }
            >
              <CloseIcon sx={{ width: 18, height: 18 }} />
            </IconButton>
            <Stack flexDirection={"row"} gap={1.2} width={"calc(100% - 26px)"}>
              <Avatar
                src={app?.createdByUser?.avatar}
                sx={{ width: 58, height: 58, borderRadius: "50%" }}
              />
              <Stack gap={0.4}>
                <Typography color={"#3182CE"} fontWeight={700}>
                  {app?.createdByUser?.name}
                </Typography>
                <Stack flexDirection={"row"} color={"primary.main"}>
                  <StarIcon sx={{ height: 16, width: 16 }} />
                  <Typography fontSize={12} color={"primary"} fontWeight={700}>
                    {Number(app.createdByUser?.averageRating).toFixed(1)}/5.0
                    <Typography component={"span"} fontSize={12} color={"common.gray5"}>
                      {" "}
                      (
                      {+app.createdByUser?.virtualOrderNumber +
                        +app.createdByUser?.completedOrderCount}{" "}
                      orders)
                    </Typography>
                  </Typography>
                </Stack>
                <Typography fontSize={12}>
                  Total earnings:{" "}
                  {roundedEarningNumber(app?.createdByUser?.totalEarnings || 0)}
                </Typography>
              </Stack>
            </Stack>
            <Typography fontSize={14}>
              {/* {app?.applyToJob?.title} */}
              Deliver by: {`${formatDurationTimeFromNow(+app?.duration)} (if start now)`}
            </Typography>
            <Typography fontSize={14} fontWeight={700}>
              Hourly rate: ${app?.expectBudget}/h
            </Typography>
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography fontSize={14} fontWeight={700} color="primary">
                Effort needed: {+app.estimation}h
              </Typography>
            </Stack>
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography
                fontSize={18}
                fontWeight={700}
                color="primary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {app?.expectBudget && "$" + +app.expectBudget * +app.estimation}
                {app?.hasNewUpdate && (
                  <Typography
                    component={"span"}
                    sx={{
                      m: 1,
                      fontSize: 10,
                      py: 0.5,
                      px: 1,
                      borderRadius: 4,
                      fontWeight: 500,
                      backgroundColor: "common.partyTime",
                      color: "common.white",
                    }}
                  >
                    Updated
                  </Typography>
                )}
              </Typography>
              <IconButton
                sx={{
                  alignSelf: "flex-end",
                }}
                onClick={(e) =>
                  handleUpdateAppStatus(
                    e,
                    "save",
                    { saved: app?.saved, archived: app?.archived },
                    app.id
                  )
                }
              >
                {app?.saved ? <BookmarkFulFillIcon /> : <BookmarkIcon />}
              </IconButton>
            </Stack>
          </Stack>

          {mouseOnApp === app.id && isUpdateAppLoading && (
            <CircleLoading
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                bgcolor: "#ffffff99",
                opacity: 0.4,
              }}
            />
          )}
        </>
      )}
    </Stack>
  );
}
